import { DEFAULT_LATEST } from 'constants/dateLimits';
import { REQUIRED } from 'constants/errors';
import { CourtMartialChargeItemModel } from 'forms/sections/military/disciplinary/types';
import {
  DateModel,
  ValidateJSModel,
} from 'forms/shared/types';
import { setToStartOfMonth } from 'helpers/date';
import { formIncludesPvqPartC } from 'helpers/form';
import { hasYesOrNo } from 'models/validate';
import { DeepSafePartial } from 'types/types';

const OUTCOME_CONSEQUENCES_VALUES: readonly string[] = [
  'Imprisonment',
  'CorrectionalCustody',
  'Detention',
  'ExtraDuties',
  'ForfeitureOfPay',
  'Other',
  'ReductionInRank',
  'Restrictions',
  'SuspendedFromDuty',
];

const OUTCOME_TYPE_VALUES: readonly string[] = [
  'AwaitingTrial',
  'ChargeDismissed',
  'ChargeDismissedAfterDeferred',
  'Guilty',
  'NolleProsequi',
  'Not guilty',
  'OnTrial',
  'Other',
];

type Attributes = DeepSafePartial<CourtMartialChargeItemModel>;

const hasCharge = (attributes: Attributes | undefined) => attributes?.Has?.value === 'Yes';

// PVQ-only
const courtMartialChargeItemValidator: ValidateJSModel<CourtMartialChargeItemModel> = {

  ConsequencesExplanation: (value, attributes) => {
    if (hasCharge(attributes)
      && attributes.OutcomeConsequences?.values?.includes?.('Other')) {
      return { hasValue: true };
    }

    return {};
  },

  CourtMartialChargeDescription: (value, attributes) => {
    if (hasCharge(attributes)) {
      return { hasValue: true };
    }

    return {};
  },

  Has: {},

  OutcomeConsequences: (value, attributes) => {
    if (hasCharge(attributes)) {
      return {
        array: {
          length: {
            message: REQUIRED,
            minimum: 1,
          },
          validator: {
            inclusion: OUTCOME_CONSEQUENCES_VALUES as string[],
            presence: true,
          }
        },
        presence: true,
      };
    }

    return {};
  },

  OutcomeExplanation: (value, attributes) => {
    if (hasCharge(attributes)
      && attributes?.OutcomeType?.value === 'Other') {
      return { hasValue: true };
    }

    return {};
  },

  OutcomeType: (value, attributes) => {
    if (hasCharge(attributes)) {
      return {
        hasValue: {
          validator: {
            inclusion: OUTCOME_TYPE_VALUES as string[],
          },
        },
      };
    }

    return {};
  },

  // PVQ Part C-only below

  ImprisonedWhen: (value, attributes, attributeName, options) => {
    if (hasCharge(attributes)
      && formIncludesPvqPartC(options?.formType)
      && attributes?.OutcomeType?.value === 'Guilty'
      && attributes.OutcomeConsequences?.values?.includes?.('Imprisonment')) {
      return {
        daterange: {
          earliest: setToStartOfMonth(options.applicantBirthdate) as DateModel,
          latest: DEFAULT_LATEST,
          requireDay: false,
        },
        presence: true,
      };
    }

    return {};
  },

  ParoleGranted: (value, attributes, attributeName, options) => {
    if (hasCharge(attributes)
      && formIncludesPvqPartC(options?.formType)
      && attributes?.OutcomeType?.value === 'Guilty'
      && attributes.OutcomeConsequences?.values?.includes?.('Imprisonment')) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },

  ParoleWhen: (value, attributes, attributeName, options) => {
    if (hasCharge(attributes)
      && formIncludesPvqPartC(options?.formType)
      && attributes?.OutcomeType?.value === 'Guilty'
      && attributes.OutcomeConsequences?.values?.includes?.('Imprisonment')
      && attributes.ParoleGranted?.value === 'Yes') {
      return {
        daterange: {
          earliest: setToStartOfMonth(options.applicantBirthdate) as DateModel,
          latest: DEFAULT_LATEST,
          requireDay: false,
        },
        presence: true,
      };
    }

    return {};
  },

};

export default courtMartialChargeItemValidator;
